import React, { useEffect, useMemo, useState } from 'react';

import PropTypes from 'prop-types';
import { useParams, withRouter } from 'react-router-dom';

import { useFetchOrganizationQuery } from 'redux/api/businessOrganizations.api';
import { useFetchCurrentOfferQuery } from 'redux/api/subscriptions.api';
import { Routing } from 'routing/routing';
import history from 'utils/history';
import localStorageUser from 'utils/localStorageUser';

export const OrganizationContext = React.createContext(null);
export const OrganizationContextUpdater = React.createContext({});

const BusinessOrganizationDataValidation = ({ children }) => {
  const params = useParams();
  const { organizationId } = params;

  // Use value from cached if it's possible
  const localStorageOrganization = localStorageUser.getBusinessOrganizationLocalStorage();
  const initialValueOrganization =
    organizationId && localStorageOrganization?.id === organizationId
      ? localStorageOrganization
      : null;

  const [currentOrganization, setCurrentOrganization] = useState(initialValueOrganization);
  const [isOrganizationLoaded, setIsOrganizationLoaded] = useState(false);

  const { currentData: organizationData, isError, isSuccess } = useFetchOrganizationQuery(
    { organizationId },
    { skip: !organizationId },
  );
  const {
    currentData: offerDetails,
    isError: isOfferError,
    isSuccess: isOfferSuccess,
  } = useFetchCurrentOfferQuery({ organizationId }, { skip: !organizationId });

  useEffect(
    () => {
      if (!organizationId) {
        setIsOrganizationLoaded(true);
        setCurrentOrganization(null);
      } else if (organizationId !== currentOrganization?.id) {
        setIsOrganizationLoaded(false);
      }
    },
    [organizationId],
  );

  useEffect(
    () => {
      if (isSuccess && organizationData && (isOfferSuccess || isOfferError)) {
        const enrichedOrganization = {
          ...organizationData,
          offerDetails: isOfferSuccess ? offerDetails : null,
        };

        localStorageUser.setBusinessOrganizationLocalStorage(enrichedOrganization);
        setCurrentOrganization(enrichedOrganization);
        setIsOrganizationLoaded(true);
      }
    },
    [isSuccess, organizationData, isOfferSuccess, isOfferError, offerDetails],
  );

  useEffect(
    () => {
      if (isError) {
        setIsOrganizationLoaded(true);
        history.push(Routing.httpRedirect.status404());
      }
    },
    [isError],
  );

  const value = useMemo(
    () => ({
      organization: currentOrganization,
      setOrganization: setCurrentOrganization,
    }),
    [currentOrganization],
  );

  return (
    <OrganizationContext.Provider value={currentOrganization}>
      <OrganizationContextUpdater.Provider value={value}>
        {isOrganizationLoaded && children}
      </OrganizationContextUpdater.Provider>
    </OrganizationContext.Provider>
  );
};

BusinessOrganizationDataValidation.propTypes = {
  children: PropTypes.node.isRequired,
};

export default withRouter(BusinessOrganizationDataValidation);
